/**
 * The interpreter — the engine side of SPEC.md §5.1.
 *
 * Triggered, activated and static abilities all run through the **resolution stack**, never
 * through suspended JS call frames. One effect tree is walked by one `effect` frame:
 *
 * ```
 * resolve-ability { item }        ← one ability, start to finish; a GSC follows (CR §6.7.5)
 *   effect { queue, bindings, awaiting }   ← the IR being walked; blocks on player choices
 * ```
 *
 * `queue` is the remaining IR in written order, `bindings` are the cards chosen so far and
 * `awaiting` is the decision the walk is blocked on. All of it is plain data, so a state
 * paused halfway through "banish chosen character, then draw a card" clones, serializes and
 * searches like any other (SPEC.md §4.3).
 *
 * ## Where abilities come from
 *
 * `abilitiesOf` merges the hand-authored IR the catalog serves (`packages/cards`) with the
 * abilities the engine derives from printed **keywords** — Support's quest trigger and
 * Vanish's chosen-by-an-opponent trigger. A keyword-only card therefore needs no module at
 * all, which is the 231-card zero-authoring share the ability analysis found (§1.1).
 *
 * ## Choices
 *
 * Every decision an effect raises is an ordinary `ChoiceRequest` with an explicit option
 * list, keyed canonically (`ability-target:i12`, `may:yes`, `ability-mode:m0`). That is the
 * invariant the whole design hangs on: opaque *effects* are tolerable, opaque *choices* are
 * not, so the AI, the UI and the replay log drive a card ability exactly as they drive a
 * quest.
 */
import type { ChoiceOption, ChoiceRequest } from '../choices.ts';
import { option } from '../choices.ts';
import { canPayExertCost, definitionOf } from '../characteristics.ts';
import { abilityInkCostOf, addCostModifier } from '../costs.ts';
import type { InstanceId, PlayerIndex, Zone } from '../ids.ts';
import type {
  CardInstance,
  Frame,
  GameState,
  PlayerBinding,
  TargetBinding,
} from '../state.ts';
import { allPlayersMask, instanceAt, playerAt, playerBit, turnOrderFrom, zoneOf } from '../state.ts';
import {
  addModifier,
  applyModifierEffect,
  applyPlayerPermissionEffect,
  applyReadyCapEffect,
  expireModifiers,
  grantedAbilities,
  normaliseAbilityName,
  setTriggerRefresh,
  suppressedAbilityNames,
} from '../modifiers.ts';
import { nextIntBelow, shuffleInPlace } from '../rng.ts';
import { boostUnder, putCardUnder } from '../stacks.ts';
import {
  availableInk,
  banishEvent,
  dealDamage,
  discardCard,
  discardPrevented,
  drawCards,
  emit,
  exertCharacter,
  findInHandByDef,
  gainLoreEvent,
  lookAtCard,
  lookAtDeck,
  loseLore,
  moveCardTo,
  moveDamage,
  moveToLocation,
  payInk,
  putIntoHand,
  putIntoInkwell,
  putOnDeck,
  removeDamage,
  returnToHand,
  revealTo,
  setDiscardRegistration,
  shuffleDeck,
  stopLooking,
} from '../zones.ts';
import type { AbilityInstance } from '../bag.ts';
import { registerTrigger, unregisterTriggersFrom } from '../bag.ts';
import { customRequest, driveCustom } from './custom.ts';
import * as dsl from './dsl.ts';
import type {
  Ability,
  ActivatedAbility,
  ActivationCost,
  CardRef,
  Condition,
  DistributeSpec,
  Effect,
  Selector,
} from './ir.ts';
import { cloneIr } from './ir.ts';
import { keywordsOf } from './keywords.ts';
import type { AbilityContext } from './selectors.ts';
import {
  baseContext,
  chooserOf,
  evalAmount,
  evalCondition,
  playerPool,
  resolveCardRefAllIn,
  resolvePlayerRef,
  selectorPool,
  targetPool,
} from './selectors.ts';

/** The `effect` frame, narrowed. */
export type EffectFrame = Extract<Frame, { kind: 'effect' }>;

// ---------------------------------------------------------------------------
// A card's abilities
// ---------------------------------------------------------------------------

/**
 * Support (CR §8.12): "Whenever this character quests, you may add their {S} to another
 * chosen character's {S} this turn." Engine-native — a card only declares the keyword.
 */
const SUPPORT_ABILITY: Ability = dsl.named(
  'Support',
  dsl.onQuest(
    dsl.may(
      dsl.strengthBonus(dsl.theirStrength(dsl.self), dsl.chosen.otherCharacter(), 'this-turn'),
    ),
  ),
);

/**
 * Vanish (CR §8.14): "When this character is chosen by an opponent as part of resolving an
 * action's effect, banish them." The `card-chosen` event is emitted by every target binding,
 * and the guard is what narrows it to an opponent's *action*.
 */
const VANISH_ABILITY: Ability = dsl.named(
  'Vanish',
  dsl.triggered(
    {
      event: 'card-chosen',
      subject: 'self',
      where: 'in-play',
      guard: dsl.allOf(dsl.byOpponent(), dsl.causedBy('Action')),
    },
    dsl.banish(dsl.self),
  ),
);

/**
 * Boost N {I} (CR §8.3): "once during your turn, you may pay N {I} to put the top card of
 * your deck facedown under this card." Engine-native like Support and Vanish, but its cost
 * depends on the printed number, so one ability is built per value and kept — the same object
 * every time, because `abilitiesOf` runs on every characteristic read and the ability's index
 * in the list is what an `activate:i7:a2` key names.
 */
const BOOST_ABILITIES = new Map<number, Ability>();

function boostAbility(value: number): Ability {
  const existing = BOOST_ABILITIES.get(value);
  if (existing !== undefined) return existing;
  const ability = dsl.named(
    'Boost',
    dsl.activated(
      { costs: [dsl.inkCost(value)], limit: 'once-per-turn', window: 'your-turn' },
      dsl.putUnderFromDeck(),
    ),
  );
  BOOST_ABILITIES.set(value, ability);
  return ability;
}

/**
 * Every ability of one card in this game: the hand-authored IR the catalog serves, plus the
 * abilities the engine derives from printed keywords — minus whatever a continuous
 * `suppress-ability` static has taken away by name (ticket 118, CR §6.6.5's "loses" pattern).
 * Suppression is absolute, like a lost keyword: it drops a matching ability whether it is
 * authored or granted, so a card that "loses" one never sees it again from any source while
 * the suppression holds.
 */
export function abilitiesOf(state: GameState, instance: CardInstance): readonly Ability[] {
  const authored = state.catalog.abilities?.(instance.defId) ?? NO_AUTHORED;
  // One keyword read covers Support, Vanish and Boost — this runs on every characteristic
  // read, and each `hasKeyword` would recompute the whole merged keyword list.
  let support = false;
  let vanish = false;
  let hasBoost = false;
  let boost = 0;
  for (const each of keywordsOf(state, instance)) {
    if (each.keyword === 'Support') support = true;
    else if (each.keyword === 'Vanish') vanish = true;
    else if (each.keyword === 'Boost') {
      // Boost does not stack (CR §8.1.2): the highest printed value wins, as `keywordValue`.
      hasBoost = true;
      boost = Math.max(boost, each.value ?? 0);
    }
  }
  const granted = grantedAbilities(state, instance);
  const suppressed = suppressedAbilityNames(state, instance);
  if (!support && !vanish && !hasBoost && granted.length === 0 && suppressed.length === 0) {
    return authored;
  }
  const derived: Ability[] = [];
  if (support) derived.push(SUPPORT_ABILITY);
  if (vanish) derived.push(VANISH_ABILITY);
  if (hasBoost) derived.push(boostAbility(boost));
  // Abilities another card gave this one (CR §6.4.3). Appended last, so the index an
  // `activate:i7:a2` key names is stable for everything the card prints.
  for (const ability of granted) derived.push(ability);
  const merged = [...authored, ...derived];
  return suppressed.length === 0
    ? merged
    : merged.filter((ability) => !isSuppressed(ability, suppressed));
}

/** A `KeywordAbility` prints no `name` — every other kind may. */
function isSuppressed(ability: Ability, suppressed: readonly string[]): boolean {
  if (ability.kind === 'keyword') return false;
  return ability.name !== undefined && suppressed.includes(normaliseAbilityName(ability.name));
}

const NO_AUTHORED: readonly Ability[] = [];

/**
 * Register a card's triggered abilities as it enters play.
 *
 * Registrations from a previous stay in play are dropped first, so a card that bounces to
 * hand and comes back registers once, not twice. Registrations are **not** dropped when the
 * card leaves play — a "when this is banished" ability has to fire from the discard, and
 * `TriggerCondition.where` is what scopes the others to being in play (CR §6.2).
 *
 * Keyword-derived triggers ride in through `abilitiesOf`, so a keyword *granted* to a card
 * already in play registers too — `setTriggerRefresh` re-runs this whenever a keyword set
 * changes.
 */
export function registerAbilitiesFor(state: GameState, instance: CardInstance): void {
  unregisterTriggersFrom(state, instance.id);
  for (const ability of abilitiesOf(state, instance)) {
    if (ability.kind !== 'triggered') continue;
    registerTrigger(state, {
      source: instance.id,
      when: ability.when,
      effect: ability.effect,
      label: ability.name ?? '',
    });
  }
}

/**
 * Register a card's **discard-functioning** triggered abilities as it enters a discard
 * (CR §6.1.12) — "At the start of your turn, if this card is in your discard, …".
 *
 * Entering play is where an ordinary printed ability is registered, which is no use to a card
 * that was discarded straight out of a hand and has never been in play. So the discard is a
 * registration point of its own, for the `where: 'discard'` abilities only: everything else a
 * card prints stays registered exactly when it always was.
 *
 * Idempotent by construction — a previous discard registration from this card is dropped
 * first, so a card that leaves the discard and comes back registers once. Nothing else the
 * card has registered is touched, because "when this is banished" is still resolving out of
 * the discard while this runs.
 */
export function registerDiscardAbilitiesFor(state: GameState, instance: CardInstance): void {
  state.bag.triggers = state.bag.triggers.filter(
    (trigger) => !(trigger.source === instance.id && trigger.when.where === 'discard'),
  );
  for (const ability of abilitiesOf(state, instance)) {
    if (ability.kind !== 'triggered' || ability.when.where !== 'discard') continue;
    registerTrigger(state, {
      source: instance.id,
      when: ability.when,
      effect: ability.effect,
      label: ability.name ?? '',
    });
  }
}

// Wired at module load, as `setTriggerRefresh` is: `zones.ts` owns the one function that moves
// a card between zones, and this module already imports it.
setDiscardRegistration(registerDiscardAbilitiesFor);

/**
 * Re-register the triggered abilities of cards whose keyword set just changed
 * (`modifiers.ts`). Wired here because this module owns `registerAbilitiesFor`, and a
 * `modifiers.ts` that imported it would close a cycle.
 */
setTriggerRefresh((state, cards) => {
  for (const id of cards) {
    const card = instanceAt(state, id);
    if (card.zone === 'play') registerAbilitiesFor(state, card);
  }
});

// ---------------------------------------------------------------------------
// Activated abilities (CR §4.4, §6.3)
// ---------------------------------------------------------------------------

/** One activatable ability on one card: what `legalTurnActions` turns into an option. */
export interface Activation {
  readonly instance: InstanceId;
  /** Index into the card's ability list — the `activate:i<id>:a<n>` key refers to it. */
  readonly ability: number;
  /**
   * The cards a "Banish 2 of your items —" cost will take. One activation per legal set, so
   * the choice is made by picking the option rather than by answering a follow-up question.
   */
  readonly banish?: readonly InstanceId[];
  /** The cards a "⟳ one of your characters —" cost will take. Same recipe as `banish`. */
  readonly exert?: readonly InstanceId[];
  /**
   * The hand cards a "Discard a card —" cost will take — **printed ids**, not instances,
   * because a hand is hidden and two copies of one card are the same way to pay (SPEC.md §4.4).
   */
  readonly discard?: readonly number[];
}

/**
 * How many ways to pay a `banish-cards` cost are offered. The same bound `payments.ts` puts on
 * a banish alternate cost, kept local so the ability layer takes no dependency on the payment
 * layer: two of six items is 15 options, and no board a game produces reaches this.
 */
const MAX_BANISH_COMBINATIONS = 128;

/** The `GameState.abilityUses` key for a once-per-turn activated ability (CR §6.1.13.2). */
export function activationUseKey(instance: InstanceId, ability: number): string {
  return `i${instance}:a${ability}`;
}

/** The activated ability at this index of this card, or `undefined`. */
export function activatedAbilityAt(
  state: GameState,
  instance: CardInstance,
  index: number,
): ActivatedAbility | undefined {
  const ability = abilitiesOf(state, instance)[index];
  return ability !== undefined && ability.kind === 'activated' ? ability : undefined;
}

/**
 * Every activated ability the player may use right now (CR §4.4). Enumerated in a fixed
 * order — owner (self, then every other player in turn order), then play-zone order, then
 * ability index — so two identical states produce identical option lists.
 *
 * Almost every card's own zone supplies these, exactly as before ticket 133. The second loop
 * is new: it scans every *other* player's play zone for abilities whose `window` is
 * specifically `opponent-turn` (Hercules - Manipulated Hero's EVERYTHING HAS A PRICE) — the
 * non-controller's own ability to use, never the controller's. `restrictWindow` keeps this
 * scan from also picking up an `any-turn` ability, which stays controller-only (see
 * `ActivatedAbility.window`'s doc).
 */
export function legalActivations(state: GameState, player: PlayerIndex): Activation[] {
  const found: Activation[] = [];
  collectActivations(state, player, found);
  for (const owner of turnOrderFrom(state, player)) {
    if (owner !== player) collectActivations(state, owner, found, 'opponent-turn');
  }
  return found;
}

/** One owner's play zone worth of activatable abilities, appended to `found`. */
function collectActivations(
  state: GameState,
  owner: PlayerIndex,
  found: Activation[],
  restrictWindow?: ActivatedAbility['window'],
): void {
  for (const id of playerAt(state, owner).play) {
    const card = instanceAt(state, id);
    abilitiesOf(state, card).forEach((ability, index) => {
      if (ability.kind !== 'activated') return;
      if (restrictWindow !== undefined && ability.window !== restrictWindow) return;
      if (!canActivate(state, card, ability, index)) return;
      // A cost paid by banishing or exerting cards is a *choice*: one option per legal set,
      // keyed by the cards it names, so the AI and the UI see the same list (SPEC.md §4.3).
      const banishSets = banishCostSets(state, card, ability);
      const exertSets = exertCostSets(state, card, ability);
      const discardSets = discardCostSets(state, card, ability);
      for (const banish of banishSets ?? [undefined]) {
        for (const exert of exertSets ?? [undefined]) {
          for (const discard of discardSets ?? [undefined]) {
            found.push({
              instance: id,
              ability: index,
              ...(banish === undefined ? {} : { banish }),
              ...(exert === undefined ? {} : { exert }),
              ...(discard === undefined ? {} : { discard }),
            });
          }
        }
      }
    });
  }
}

/**
 * Every set of cards this ability's `banish-cards` cost could take, or `null` when it has no
 * such component. An empty array means the cost cannot be paid at all.
 */
function banishCostSets(
  state: GameState,
  card: CardInstance,
  ability: ActivatedAbility,
): InstanceId[][] | null {
  const cost = ability.costs.find((each) => each.kind === 'banish-cards');
  if (cost === undefined) return null;
  return combinationsOf(banishCostPool(state, card, cost.filter), cost.count ?? 1);
}

/** The cards a `banish-cards` cost may take: its selector's pool, in play, read from this card. */
function banishCostPool(state: GameState, card: CardInstance, filter: Selector): InstanceId[] {
  return selectorPool(state, baseContext(card.id, card.controller, null), filter).filter(
    (id) => instanceAt(state, id).zone === 'play',
  );
}

/**
 * Every set of cards this ability's `exert-cards` cost could take, or `null` when it has no
 * such component. Mirrors `banishCostSets` exactly.
 */
function exertCostSets(
  state: GameState,
  card: CardInstance,
  ability: ActivatedAbility,
): InstanceId[][] | null {
  const cost = ability.costs.find((each) => each.kind === 'exert-cards');
  if (cost === undefined) return null;
  return combinationsOf(exertCostPool(state, card, cost.filter), cost.count ?? 1);
}

/**
 * The cards an `exert-cards` cost may take: its selector's pool, restricted to cards that can
 * actually pay `{E}`. This is the same bar a card's own `{E}` cost clears, so "exert one of
 * your characters" can never spend a card that just entered play or one that is already
 * exerted — while "⟳ one of your items" accepts any ready item, dry or not.
 */
function exertCostPool(state: GameState, card: CardInstance, filter: Selector): InstanceId[] {
  return selectorPool(state, baseContext(card.id, card.controller, null), filter).filter((id) =>
    canPayExertCost(state, instanceAt(state, id)),
  );
}

/**
 * Every set of hand cards this ability's `discard-cards` cost could take, or `null` when it
 * has no such component. Sets are **printed ids** — the hand is hidden, so two copies of one
 * card are the same way to pay (SPEC.md §4.4) — deduplicated accordingly, ascending, and
 * bounded like `combinationsOf`. An empty array means the cost cannot be paid at all.
 */
function discardCostSets(
  state: GameState,
  card: CardInstance,
  ability: ActivatedAbility,
): number[][] | null {
  const cost = ability.costs.find((each) => each.kind === 'discard-cards');
  if (cost === undefined) return null;
  const hand = discardCostPool(state, card, cost.filter).map((id) => instanceAt(state, id).defId);
  return defIdCombinationsOf(hand, cost.count ?? 1);
}

/**
 * The hand cards a `discard-cards` cost may take: every card in the controller's hand, or —
 * when the cost carries a `filter` — only the ones it matches. Mirrors `banishCostPool` exactly,
 * restricted to `hand` where `banishCostPool` restricts to `play`; the filter itself must say
 * `zone: 'hand'` for the same reason `banishCost`'s filters need no zone at all (their default,
 * `play`, is already where they look).
 */
function discardCostPool(
  state: GameState,
  card: CardInstance,
  filter: Selector | undefined,
): InstanceId[] {
  if (filter === undefined) return playerAt(state, card.controller).hand;
  return selectorPool(state, baseContext(card.id, card.controller, null), filter).filter(
    (id) => instanceAt(state, id).zone === 'hand',
  );
}

/** Every distinct `size`-card multiset of printed ids, each ascending, bounded and sorted. */
function defIdCombinationsOf(pool: readonly number[], size: number): number[][] {
  if (size <= 0 || pool.length < size) return [];
  const sorted = [...pool].sort((a, b) => a - b);
  const found: number[][] = [];
  const chosen: number[] = [];
  const walk = (start: number): void => {
    if (found.length >= MAX_BANISH_COMBINATIONS) return;
    if (chosen.length === size) {
      found.push([...chosen]);
      return;
    }
    for (let index = start; index < sorted.length; index += 1) {
      // Two copies of one printed id are the same move, so branch on each id once per depth.
      if (index > start && sorted[index] === sorted[index - 1]) continue;
      chosen.push(sorted[index] as number);
      walk(index + 1);
      chosen.pop();
      if (found.length >= MAX_BANISH_COMBINATIONS) return;
    }
  };
  walk(0);
  return found;
}

/** Every `size`-card subset, ascending and bounded — the same shape `payments.ts` enumerates. */
function combinationsOf(pool: readonly InstanceId[], size: number): InstanceId[][] {
  if (size <= 0 || pool.length < size) return [];
  const found: InstanceId[][] = [];
  const chosen: InstanceId[] = [];
  const walk = (start: number): void => {
    if (found.length >= MAX_BANISH_COMBINATIONS) return;
    if (chosen.length === size) {
      found.push([...chosen].sort((a, b) => a - b));
      return;
    }
    for (let index = start; index < pool.length; index += 1) {
      chosen.push(pool[index] as InstanceId);
      walk(index + 1);
      chosen.pop();
      if (found.length >= MAX_BANISH_COMBINATIONS) return;
    }
  };
  walk(0);
  return found;
}

/** Whether one activated ability's costs can be paid and its limits allow it. */
export function canActivate(
  state: GameState,
  card: CardInstance,
  ability: ActivatedAbility,
  index: number,
): boolean {
  if (card.zone !== 'play') return false;
  const window = ability.window ?? 'your-turn';
  if (window === 'your-turn' && state.activePlayer !== card.controller) return false;
  // "During the opposing player's turn, they may …" (ticket 133) — the mirror image of
  // `your-turn`, and the same check `bag.ts`'s trigger windows use: the controller may never
  // activate this, on any turn.
  if (window === 'opponent-turn' && state.activePlayer === card.controller) return false;
  if (
    ability.limit === 'once-per-turn' &&
    state.abilityUses.includes(activationUseKey(card.id, index))
  ) {
    return false;
  }
  // "[condition], you may [cost] to [effect]" — the condition gates the activation itself,
  // read *now*, before any cost is paid: a `banish-self` cost may be about to change the very
  // board the condition describes, so a mid-resolution check could never observe it.
  if (
    ability.while !== undefined &&
    !evalCondition(state, baseContext(card.id, card.controller, null), ability.while)
  ) {
    return false;
  }
  return ability.costs.every((cost) => canPayCost(state, card, cost));
}

/**
 * Whether one component of an activated ability's cost can be paid (CR §4.4).
 *
 * The `{I}` component reads through the cost system (ticket 09), so a payment modifier scoped
 * to abilities ("your characters' activated abilities cost 1 {I} less") lands here too.
 * Components that need a *choice* to pay — banish or exert cards, discard a card — follow the
 * `payments.ts` recipe: each legal payment is its own keyed option rather than a follow-up
 * question, so this only answers whether *any* way to pay exists.
 */
function canPayCost(state: GameState, card: CardInstance, cost: ActivationCost): boolean {
  switch (cost.kind) {
    case 'exert':
      // A character must be dry to {E} (CR §4.4.2); an item or location need not be (§5.5).
      return canPayExertCost(state, card);
    case 'ink':
      return availableInk(state, card.controller) >= abilityInkCostOf(state, card, cost.amount);
    case 'banish-self':
      return true;
    // "Deal 1 damage to this character" — unconditionally payable, mirroring `banish-self`:
    // the CR has no "can't pay a cost that would banish the source" carve-out, so a lethal
    // amount is still paid in full and the normal game state check sweeps it afterward.
    case 'self-damage':
      return true;
    // "Banish 2 of your items" — payable when there are that many to banish. *Which* ones is
    // the option's own choice, so it is not decided here.
    case 'banish-cards':
      return banishCostPool(state, card, cost.filter).length >= (cost.count ?? 1);
    // "⟳ one of your characters/items" — payable when that many matching cards can pay `{E}`.
    // *Which* ones is the option's own choice, mirroring `banish-cards`.
    case 'exert-cards':
      return exertCostPool(state, card, cost.filter).length >= (cost.count ?? 1);
    // "Discard a card" — payable when the hand (or, filtered, the matching part of it) is that
    // big. An unpayable cost makes the whole activation illegal (CR §4.4.3), so the option is
    // simply absent, never a no-op.
    case 'discard-cards':
      return discardCostPool(state, card, cost.filter).length >= (cost.count ?? 1);
    // "…may pay 3 lore —" (ticket 133) — payable when *the activator* (always `state
    // .activePlayer`: only the active player is ever offered an activation, so this is the
    // controller for an ordinary ability and the non-controller for an `opponent-turn` one)
    // has that much lore. Unlike `self-damage`/`banish-self` there is no "pay it anyway"
    // reading — lore cannot go negative (CR §6.6.3), so an unaffordable amount makes the whole
    // activation illegal, exactly like `banish-cards`/`exert-cards`/`discard-cards`.
    case 'pay-lore':
      return playerAt(state, state.activePlayer).lore >= cost.amount;
  }
}

/**
 * Pay an activated ability's costs and put its resolution on the stack.
 *
 * **Activated abilities never enter the bag** (CR §7.7): the `resolve-ability` frame goes
 * straight onto the resolution stack, so the effect resolves immediately and in full, and the
 * triggers it raises wait for the next open bag window (CR §6.3).
 */
export function startActivation(state: GameState, activation: Activation): void {
  const card = instanceAt(state, activation.instance);
  const ability = activatedAbilityAt(state, card, activation.ability);
  if (ability === undefined) {
    throw new Error(`Instance ${card.id} has no activated ability ${activation.ability}`);
  }
  if (!canActivate(state, card, ability, activation.ability)) {
    throw new Error(`Instance ${card.id} cannot activate ability ${activation.ability}`);
  }
  const controller = card.controller;
  const defId = card.defId;
  for (const cost of ability.costs) {
    payCost(state, card, cost, activation.banish, activation.exert, activation.discard);
  }
  if (ability.limit === 'once-per-turn') {
    state.abilityUses.push(activationUseKey(card.id, activation.ability));
  }
  emit(state, {
    type: 'ability-activated',
    // The activator, not necessarily the card's controller — `state.activePlayer` at this
    // point in `apply` is whoever chose the `activate` turn action (only the active player
    // ever is), which for an `opponent-turn` ability (ticket 133) is the non-controller. Every
    // pre-existing `your-turn`/`any-turn` activation has `state.activePlayer === controller`
    // by `canActivate`'s own gate, so this is behavior-preserving for every card before ticket
    // 133.
    player: state.activePlayer,
    source: card.id,
    defId,
    ability: activation.ability,
    label: ability.name ?? '',
  });
  pushAbility(state, {
    id: -1,
    controller,
    source: card.id,
    subject: null,
    effect: cloneIr(ability.effect) as Effect[],
    label: ability.name ?? '',
    origin: 'activated',
    // The cards a "Banish one of your … —" cost took ride in already bound, exactly as a sung
    // song's `singers` do: the effect resolves after they are gone from play, and "the
    // banished character" is a card no selector could find again. `bound('cost-banished')`
    // reads them; an ability with no such cost simply finds nothing bound (CR §1.2.3).
    ...(activation.banish === undefined
      ? {}
      : { bindings: [{ id: 'cost-banished', instances: [...activation.banish] }] }),
  });
}

function payCost(
  state: GameState,
  card: CardInstance,
  cost: ActivationCost,
  banishing: readonly InstanceId[] | undefined,
  exerting: readonly InstanceId[] | undefined,
  discarding: readonly number[] | undefined,
): void {
  switch (cost.kind) {
    case 'exert':
      exertCharacter(state, card.id);
      return;
    case 'ink':
      payInk(state, card.controller, abilityInkCostOf(state, card, cost.amount));
      return;
    case 'banish-self':
      banishEvent(state, card.id);
      return;
    // Same pipeline any "deal damage" effect uses — Resist reduces it, replacements see it —
    // paid immediately; a lethal result is caught by the game state check that follows
    // activation (`case 'activate'` in `engine.ts`), not by this function.
    case 'self-damage':
      dealDamage(state, card.id, cost.amount, card.id, 'deal');
      return;
    case 'banish-cards': {
      const wanted = cost.count ?? 1;
      const pool = banishCostPool(state, card, cost.filter);
      const chosen = banishing ?? [];
      if (chosen.length !== wanted || !chosen.every((id) => pool.includes(id))) {
        throw new Error(`Instance ${card.id} was activated without a legal banish cost`);
      }
      for (const id of chosen) banishEvent(state, id, card.id);
      return;
    }
    case 'exert-cards': {
      const wanted = cost.count ?? 1;
      const pool = exertCostPool(state, card, cost.filter);
      const chosen = exerting ?? [];
      if (chosen.length !== wanted || !chosen.every((id) => pool.includes(id))) {
        throw new Error(`Instance ${card.id} was activated without a legal exert cost`);
      }
      for (const id of chosen) exertCharacter(state, id);
      return;
    }
    case 'discard-cards': {
      const wanted = cost.count ?? 1;
      const pool = discardCostPool(state, card, cost.filter).map((id) => instanceAt(state, id).defId);
      const chosen = discarding ?? [];
      if (chosen.length !== wanted || !chosen.every((defId) => pool.includes(defId))) {
        throw new Error(`Instance ${card.id} was activated without a legal discard cost`);
      }
      // Chosen by printed id — the hand is hidden — so each resolves to the first matching
      // instance in hand order, exactly as `answerDiscard` resolves a `discard:d…` key.
      // Discarding one by one keeps two copies of the same printed id honest.
      for (const defId of chosen) {
        const held = findInHandByDef(state, card.controller, defId);
        if (held === undefined) {
          throw new Error(`Instance ${card.id} was activated without a legal discard cost`);
        }
        discardCard(state, held.id);
      }
      return;
    }
    // Through the same chokepoint any other lore loss goes through (ticket 57's sibling to
    // `gainLoreEvent`), paid by the activator — see `canPayCost`'s `pay-lore` case for why that
    // is `state.activePlayer` and not `card.controller`.
    case 'pay-lore':
      loseLore(state, state.activePlayer, cost.amount, card.id);
      return;
  }
}

// ---------------------------------------------------------------------------
// Resolving an ability
// ---------------------------------------------------------------------------

/**
 * How "play that card for free" reaches `rules.ts`'s play machinery. Set once, at module load,
 * by `rules.ts` — the same deferred wiring as `setTriggerRefresh`, and for the same reason:
 * `rules.ts` imports this module, so importing it back would close a cycle.
 */
export type FreePlay = (
  state: GameState,
  card: InstanceId,
  exerted: boolean,
  destination?: 'bottom-of-deck',
) => void;

let freePlay: FreePlay = () => {};

export function setFreePlay(play: FreePlay): void {
  freePlay = play;
}

/** Effect-triggered Shift's bridge to `rules.ts`, parallel to immediate free play. */
export type FreeShift = (state: GameState, card: InstanceId, onto: InstanceId) => void;

let freeShift: FreeShift = () => {};

export function setFreeShift(shift: FreeShift): void {
  freeShift = shift;
}

/**
 * The paying twin: how "you may play her" — for her real cost — reaches the same machinery.
 * Wired the same way, by `rules.ts`, at module load.
 */
export type PaidPlay = (state: GameState, card: InstanceId, exerted: boolean) => void;

let paidPlay: PaidPlay = () => {};

export function setPaidPlay(play: PaidPlay): void {
  paidPlay = play;
}

/** Put one ability's resolution on the stack. */
export function pushAbility(state: GameState, item: AbilityInstance): void {
  state.stack.push({ kind: 'resolve-ability', item, started: false });
}

/** Open the `effect` frame that walks one ability's IR. */
export function pushEffectFrame(state: GameState, item: AbilityInstance): void {
  state.stack.push({
    kind: 'effect',
    source: item.source,
    controller: item.controller,
    subject: item.subject,
    eventSource: item.eventSource ?? null,
    eventAmount: item.eventAmount ?? null,
    eventDefender: item.eventDefender ?? null,
    label: item.label,
    // Cards the *event* named ride in already bound — a sung song's `singers`, which no
    // selector could describe and no player chose (`bag.ts`'s `eventBindings`).
    bindings: (item.bindings ?? []).map((each) => ({ id: each.id, instances: [...each.instances] })),
    resultBindings: [],
    playerBindings: [],
    queue: cloneIr(item.effect) as Effect[],
    awaiting: null,
    custom: null,
  });
}

function contextOf(frame: EffectFrame): AbilityContext {
  return {
    source: frame.source,
    controller: frame.controller,
    subject: frame.subject,
    eventSource: frame.eventSource,
    eventAmount: frame.eventAmount,
    eventDefender: frame.eventDefender,
    bindings: frame.bindings,
    resultBindings: frame.resultBindings,
    playerBindings: frame.playerBindings,
  };
}

/**
 * Perform one step of an effect frame: answer the choice it is blocked on, run the next IR
 * node, or pop when the queue is empty. Returns the `ChoiceRequest` the frame is waiting on,
 * or `null` if it made progress on its own.
 */
export function stepEffectFrame(state: GameState, frame: EffectFrame): ChoiceRequest | null {
  if (frame.awaiting !== null) return effectRequest(state, frame);
  const next = frame.queue.shift();
  if (next === undefined) {
    const top = state.stack.pop();
    if (top !== frame) throw new Error('The effect frame was not on top of the resolution stack');
    return null;
  }
  runNode(state, frame, next);
  return null;
}

/** Rebuild the request an effect frame is blocked on. `null` when it is not blocked. */
export function effectRequest(state: GameState, frame: EffectFrame): ChoiceRequest | null {
  const awaiting = frame.awaiting;
  if (awaiting === null) return null;
  switch (awaiting.kind) {
    case 'target': {
      const pool = targetPool(state, contextOf(frame), awaiting.spec, awaiting.picked);
      const options: ChoiceOption[] = pool.map((id) =>
        option({ kind: 'ability-target', instance: id }),
      );
      // "Up to N" includes 0, and a partially-filled requirement may always be stopped short
      // once the pool runs dry (CR §6.1.3, §1.2.3 "do as much as you can").
      if (awaiting.spec.upTo === true || pool.length === 0) {
        options.push(option({ kind: 'ability-target-done' }));
      }
      // Who answers: the controller, unless the spec says somebody else does — "each opponent
      // chooses and banishes one of their characters" (`TargetSpec.chooser`). The pool is read
      // relative to the controller either way; only the decision moves.
      return {
        kind: 'ability-target',
        player: chooserOf(state, contextOf(frame), awaiting.spec),
        options,
      };
    }
    // "Remove up to N damage total from any number of your characters" (ticket 135) — two
    // rounds per pick, told apart by `pendingTarget`: which character next (reusing
    // `ability-target`/`ability-target-done`, exactly as `target` asks it), then how much of
    // the remaining pool to spend on it (`ability-amount`).
    case 'distribute': {
      const ctx = contextOf(frame);
      if (awaiting.pendingTarget === null) {
        const pool = distributeCandidates(state, ctx, awaiting.spec, awaiting.picked);
        const options: ChoiceOption[] = pool.map((id) =>
          option({ kind: 'ability-target', instance: id }),
        );
        // "Up to N" always allows stopping short, the same as a `target` node's own (CR §6.1.3).
        options.push(option({ kind: 'ability-target-done' }));
        return { kind: 'ability-target', player: frame.controller, options };
      }
      // Committed to this character; how much of the pool it can still absorb — never more
      // than its own current damage, so every offered amount is fully spendable.
      const cap = Math.min(awaiting.remaining, instanceAt(state, awaiting.pendingTarget).damage);
      const options: ChoiceOption[] = [];
      for (let value = 1; value <= cap; value += 1) {
        options.push(option({ kind: 'ability-amount', value }));
      }
      return { kind: 'ability-amount', player: frame.controller, options };
    }
    case 'choose-player': {
      const pool = playerPool(state, contextOf(frame), awaiting.spec);
      return {
        kind: 'choose-player',
        player: chooserOf(state, contextOf(frame), awaiting.spec),
        options: pool.map((each) => option({ kind: 'choose-player', player: each })),
      };
    }
    case 'may':
      // Who is asked: the controller, unless the node names somebody else — "…unless their
      // player pays 3 {I}", where the offer belongs to the challenging character's controller
      // (`Effect.may`'s `chooser`). Resolved here rather than stored, so the request is rebuilt
      // from data on every step exactly as a targeting request's pool is.
      return {
        kind: 'may',
        player:
          awaiting.chooser === undefined
            ? frame.controller
            : resolvePlayerRef(state, contextOf(frame), awaiting.chooser),
        options: [option({ kind: 'may', accept: true }), option({ kind: 'may', accept: false })],
      };
    case 'modal':
      return {
        kind: 'ability-mode',
        player: frame.controller,
        options: awaiting.modes.map((_mode, index) => option({ kind: 'ability-mode', mode: index })),
      };
    case 'custom-choice':
      // The escape hatch raises an ordinary request like everything else (SPEC.md §5.2).
      return customRequest(state, frame, awaiting.spec);
    case 'discard': {
      // A hand is hidden, so the options are the *distinct printed ids* in it — two copies of
      // one card are the same move (SPEC.md §4.4).
      const seen = new Set<number>();
      for (const id of playerAt(state, awaiting.player).hand) {
        seen.add(instanceAt(state, id).defId);
      }
      return {
        kind: 'discard',
        player: awaiting.player,
        options: [...seen]
          .sort((a, b) => a - b)
          .map((defId) => option({ kind: 'discard', defId })),
      };
    }
  }
}

/** Answer a pending "choose and discard a card" (CR §7.3). */
export function answerDiscard(state: GameState, frame: EffectFrame, defId: number): void {
  const awaiting = frame.awaiting;
  if (awaiting === null || awaiting.kind !== 'discard') {
    throw new Error('No discard choice is pending');
  }
  const card = findInHandByDef(state, awaiting.player, defId);
  if (card === undefined) {
    throw new Error(`Player ${awaiting.player} has no card ${defId} in hand to discard`);
  }
  discardCard(state, card.id);
  awaiting.remaining -= 1;
  if (awaiting.remaining <= 0 || playerAt(state, awaiting.player).hand.length === 0) {
    frame.awaiting = null;
  }
}

/** Push effects to the front of the queue, so they run before whatever follows. */
function unshift(frame: EffectFrame, ...effects: Effect[]): void {
  frame.queue.unshift(...effects);
}

/**
 * Say that a sentence was passed over, and why (`events.ts`'s `effect-skipped`).
 *
 * Narration only — this is the one place in the interpreter that emits an event nothing in
 * the engine reads. It runs at the three `return`s that legally do nothing, so the UI can tell
 * a trigger that did nothing from one that was never resolved at all.
 */
function skipped(
  state: GameState,
  frame: EffectFrame,
  why:
    | { reason: 'condition'; condition: Condition['kind'] }
    | { reason: 'no-target' }
    | { reason: 'declined' },
): void {
  emit(state, {
    type: 'effect-skipped',
    player: frame.controller,
    source: frame.source,
    defId: instanceAt(state, frame.source).defId,
    ability: frame.label,
    ...why,
  });
}

function runNode(state: GameState, frame: EffectFrame, node: Effect): void {
  const ctx = contextOf(frame);
  switch (node.kind) {
    case 'noop':
      return;
    case 'seq':
      unshift(frame, ...node.steps);
      return;
    case 'if':
      if (evalCondition(state, ctx, node.condition)) unshift(frame, node.then);
      else if (node.otherwise !== undefined) unshift(frame, node.otherwise);
      else skipped(state, frame, { reason: 'condition', condition: node.condition.kind });
      return;
    case 'may':
      frame.awaiting = {
        kind: 'may',
        then: node.then,
        ...(node.label === undefined ? {} : { label: node.label }),
        ...(node.chooser === undefined ? {} : { chooser: node.chooser }),
        ...(node.otherwise === undefined ? {} : { otherwise: node.otherwise }),
      };
      return;
    case 'modal': {
      const only = node.modes[0];
      if (node.modes.length === 1 && only !== undefined) {
        unshift(frame, only.effect);
        return;
      }
      frame.awaiting = { kind: 'modal', modes: node.modes };
      return;
    }
    case 'target': {
      const pool = targetPool(state, ctx, node.spec);
      // "How many to pick" — a dynamic `Amount` (what "the rest of what you are looking at"
      // needs) reads the same way every other one does: nothing this node names has moved
      // yet, so evaluating it here and again on each later step (`chooseTarget`) reads the
      // same state and answers the same number.
      const wanted = evalAmount(state, ctx, node.spec.count ?? 1);
      // "If no legal choice exists, the effect resolves with no effect" (CR §6.7.3) — the
      // rest of this sentence is skipped, exactly as a declined `may` is.
      if (pool.length === 0) {
        skipped(state, frame, { reason: 'no-target' });
        return;
      }
      // A forced choice with one legal answer is not a decision; binding it here keeps the
      // AI's branching factor honest and the action log free of no-op entries.
      if (wanted === 1 && pool.length === 1 && node.spec.upTo !== true) {
        bindTarget(state, frame, node.spec.id, [pool[0] as InstanceId]);
        unshift(frame, node.then);
        return;
      }
      frame.awaiting = { kind: 'target', spec: node.spec, picked: [], then: node.then };
      return;
    }
    // "Remove up to N damage total from any number of your characters" (ticket 135) — the
    // distribute-a-pool decision. Opens only against candidates that currently carry damage
    // (an undamaged character can never receive any of the pool) and a positive pool; either
    // missing skips the sentence exactly as an empty `target` pool does (CR §6.7.3).
    case 'distribute-remove-damage': {
      const pool = evalAmount(state, ctx, node.spec.pool);
      const candidates = pool > 0 ? distributeCandidates(state, ctx, node.spec, []) : [];
      if (pool <= 0 || candidates.length === 0) {
        skipped(state, frame, { reason: 'no-target' });
        return;
      }
      frame.awaiting = {
        kind: 'distribute',
        spec: node.spec,
        ...(node.as === undefined ? {} : { as: node.as }),
        remaining: pool,
        spent: 0,
        picked: [],
        pendingTarget: null,
      };
      return;
    }
    // "Chosen player"/"chosen opponent" (ticket 102) — `target`'s player-scoped twin. The
    // 2-player product (SPEC.md §4.6) means an `opponent`-pool spec always has exactly one
    // candidate, so it auto-binds by the same single-candidate rule as a one-card `target`
    // pool; an `any`-pool spec has two (you, or the opponent) and genuinely asks.
    case 'choose-player': {
      const pool = playerPool(state, ctx, node.spec);
      if (pool.length === 1) {
        bindPlayer(state, frame, node.spec.id, pool[0] as PlayerIndex);
        unshift(frame, node.then);
        return;
      }
      frame.awaiting = { kind: 'choose-player', spec: node.spec, then: node.then };
      return;
    }
    // "Each player may draw a card" (Donald Duck - Perfect Gentleman, ticket 125). No decision
    // about *who* — only what happens to each — so this expands eagerly into the flat sequence
    // [bind p0, then, bind p1, then, …] rather than raising an `awaiting` of its own; whatever
    // `then` itself asks (the nested `may`) raises its own request exactly as it would standing
    // alone, and fully resolves before the next player's bind runs (`resolvePlayerRef`'s
    // `chosen` case reads the *last* binding for the id).
    case 'each-player': {
      const order = turnOrderFrom(state, state.activePlayer);
      const steps: Effect[] = [];
      for (const player of order) {
        steps.push({ kind: 'bind-each-player', id: node.id, player });
        steps.push(node.then);
      }
      unshift(frame, ...steps);
      return;
    }
    case 'bind-each-player':
      frame.playerBindings.push({ id: node.id, player: node.player });
      return;
    case 'custom':
      // The escape hatch (SPEC.md §5.2). `driveCustom` re-creates the registered generator,
      // fast-forwards it over the frame's transcript and carries out its next yield — which
      // is why a state paused mid-`custom()` clones and resumes like any other (§4.3).
      driveCustom(state, frame, node);
      return;
    default:
      runPrimitive(state, frame, ctx, node);
  }
}

/**
 * The primitive verbs. Every one is "do as much as you can" (CR §1.2.3): a target that has
 * left play is skipped, not an error.
 */
function runPrimitive(
  state: GameState,
  frame: EffectFrame,
  ctx: AbilityContext,
  node: Effect,
): void {
  switch (node.kind) {
    // "Gain 1 lore" — through the same chokepoint questing uses, so a "can't gain lore"
    // restriction and the replacement pipeline see every gain alike (CR §6.5, §6.6.5).
    case 'gain-lore':
      gainLoreEvent(
        state,
        resolvePlayerRef(state, ctx, node.player),
        evalAmount(state, ctx, node.amount),
        frame.source,
      );
      return;
    // "Each opponent loses 1 lore" — through the replacement pipeline, so a "you can't lose
    // lore" replacement can cancel it before any lore moves (CR §6.5, §6.6.3).
    case 'lose-lore':
      loseLore(
        state,
        resolvePlayerRef(state, ctx, node.player),
        evalAmount(state, ctx, node.amount),
        frame.source,
      );
      return;
    case 'draw':
      drawCards(
        state,
        resolvePlayerRef(state, ctx, node.player),
        evalAmount(state, ctx, node.count),
      );
      return;
    case 'deal-damage':
    case 'put-damage': {
      const amount = evalAmount(state, ctx, node.amount);
      const verb = node.kind === 'deal-damage' ? 'deal' : 'put';
      const ignoresResist = node.kind === 'deal-damage' ? node.ignoresResist : undefined;
      for (const id of pooled(state, ctx, node)) {
        dealDamage(
          state,
          id,
          amount,
          frame.source,
          verb,
          ignoresResist === undefined ? {} : { ignoresResist },
        );
      }
      return;
    }
    case 'remove-damage': {
      const amount = evalAmount(state, ctx, node.amount);
      // `node.as` needs what actually happened, not the requested `amount` — the pipeline can
      // clamp it to the damage present or rewrite it via a replacement (`damage.ts`), and the
      // `damage-removed` events it emits along the way are the one place that number lands.
      const before = node.as === undefined ? 0 : state.events.length;
      for (const id of pooled(state, ctx, node)) removeDamage(state, id, amount);
      if (node.as !== undefined) {
        const removed = state.events
          .slice(before)
          .reduce((total, event) => (event.type === 'damage-removed' ? total + event.amount : total), 0);
        frame.resultBindings.push({ id: node.as, amount: removed });
      }
      return;
    }
    case 'move-damage': {
      const amount = evalAmount(state, ctx, node.amount);
      const to = inPlay(state, ctx, node.to)[0];
      if (to === undefined) return;
      // `fromScope`'s pool is a snapshot taken once, exactly like `deal-damage`'s `scope` — a
      // source this loop moves damage off of never leaves play as a side effect, but the pool
      // itself still reads as "evaluated once, as the effect resolves" (CR §6.7.3).
      const sources =
        node.fromScope !== undefined
          ? selectorPool(state, ctx, node.fromScope).filter(
              (id) => instanceAt(state, id).zone === 'play',
            )
          : inPlay(state, ctx, node.from as CardRef);
      for (const id of sources) {
        moveDamage(state, id, to, amount, frame.source);
      }
      return;
    }
    case 'banish':
      for (const id of pooled(state, ctx, node)) banishEvent(state, id, frame.source);
      return;
    // Exert and ready also reach the **inkwell** — "exert all cards in your inkwell" — which
    // is exactly the two zones an exerted state exists in (`zones.ts`). A selector still has
    // to *ask* for the inkwell (`zone: 'inkwell'`); the default pool is play, as ever.
    case 'exert':
      for (const id of pooled(state, ctx, node, EXERTABLE_ZONES)) {
        exertCharacter(state, id);
      }
      return;
    case 'ready':
      for (const id of pooled(state, ctx, node, EXERTABLE_ZONES)) {
        const card = instanceAt(state, id);
        if (!card.exerted) continue;
        card.exerted = false;
        // "Whenever you ready this character" reads the same event the Ready step emits.
        emit(state, {
          type: 'card-readied',
          player: card.controller,
          instance: id,
          defId: card.defId,
        });
      }
      return;
    // "Return this card to your hand" also reaches a card in the **discard**: the ability that
    // says it is a "when this is banished" trigger, and by the time it resolves the card is
    // already there (CR §6.1.12).
    case 'return-to-hand': {
      const returning =
        node.scope !== undefined
          ? selectorPool(state, ctx, node.scope)
          : node.target === undefined
            ? []
            : resolveCardRefAllIn(state, ctx, node.target);
      for (const id of returning) {
        const zone = instanceAt(state, id).zone;
        if (zone === 'play' || zone === 'discard') returnToHand(state, id);
      }
      return;
    }
    case 'move-to-location': {
      const location = inPlay(state, ctx, node.location)[0];
      if (location === undefined) return;
      if (definitionOf(state, instanceAt(state, location)).type !== 'Location') return;
      for (const id of inPlay(state, ctx, node.target)) {
        if (definitionOf(state, instanceAt(state, id)).type !== 'Character') continue;
        moveToLocation(state, id, location);
      }
      return;
    }
    // Boost's put-under (CR §8.3). The card joins the target's pile facedown; `stacks.ts`
    // owns the pile and the "nobody may look at it" rule. A `scope` snapshots an ordered
    // group instead of a single target ("… under each of your other characters"); `as`
    // binds whichever of them actually received a card (an undersized deck or a recipient
    // that left play mid-iteration leaves some out) for a coupled follow-up effect.
    case 'put-under-from-deck': {
      const recipients: InstanceId[] = [];
      for (const id of pooled(state, ctx, node)) {
        if (boostUnder(state, id) !== null) recipients.push(id);
      }
      if (node.as !== undefined) frame.bindings.push({ id: node.as, instances: recipients });
      return;
    }
    // The Black Cauldron's put-under (ticket 63): a **named** card, from wherever it was
    // chosen (its own selector already enforced the zone), joins the pile faceup.
    case 'put-card-under': {
      const under = inPlay(state, ctx, node.under)[0];
      if (under === undefined) return;
      for (const id of resolveCardRefAllIn(state, ctx, node.target)) putCardUnder(state, id, under);
      return;
    }
    // -- looking at, and rearranging, a deck (CR §7.9, §7.2) ------------------
    case 'look-at-deck':
      lookAtDeck(
        state,
        resolvePlayerRef(state, ctx, node.player ?? { kind: 'controller' }),
        evalAmount(state, ctx, node.count),
        node.reveal === true,
      );
      return;
    case 'stop-looking':
      stopLooking(state, resolvePlayerRef(state, ctx, node.player ?? { kind: 'controller' }));
      return;
    case 'put-into-hand':
      for (const id of resolveCardRefAllIn(state, ctx, node.target)) putIntoHand(state, id);
      return;
    case 'put-on-deck':
      for (const id of resolveCardRefAllIn(state, ctx, node.target)) {
        putOnDeck(state, id, node.placement);
      }
      return;
    case 'put-into-inkwell': {
      const into = resolvePlayerRef(state, ctx, node.player ?? { kind: 'controller' });
      for (const id of resolveCardRefAllIn(state, ctx, node.target)) {
        putIntoInkwell(state, id, into, {
          ...(node.exerted === undefined ? {} : { exerted: node.exerted }),
          ...(node.facedown === undefined ? {} : { facedown: node.facedown }),
        });
      }
      return;
    }
    /**
     * The general zone verb. Unlike every other board verb it must **not** be filtered to
     * cards in play: relocating the cards that are not in play is its whole job.
     */
    case 'move-card': {
      const moving = anywhere(state, ctx, node);
      // "…in a random order" (ticket 103): randomize only the order the *moved* cards land in,
      // read off the engine's own seeded RNG (SPEC.md §4.2) so a replay from the same seed
      // reorders them identically — the whole rest of the destination zone is untouched, unlike
      // `shuffle` below, which reshuffles the entire destination deck.
      if (node.randomizeOrder === true) shuffleInPlace(moving, state.rng);
      // Moving a card out of a hand into a discard *is* discarding it (CR §7.3), so it is
      // offered to the replacement pipeline first — per player, since one effect can empty
      // several hands and a replacement only spares the hand it is about. Every other
      // relocation, and every card that is not in a hand, moves as usual.
      const spared = node.to === 'discard' ? preventedHands(state, moving, frame.source) : null;
      for (const id of moving) {
        const card = instanceAt(state, id);
        if (spared !== null && card.zone === 'hand' && spared.has(card.controller)) continue;
        moveCardTo(state, id, {
          to: node.to,
          ...(node.placement === undefined ? {} : { placement: node.placement }),
          ...(node.exerted === undefined ? {} : { exerted: node.exerted }),
          ...(node.facedown === undefined ? {} : { facedown: node.facedown }),
          ...(node.shuffle === undefined ? {} : { shuffle: node.shuffle }),
        });
      }
      return;
    }
    // "Look at the top 4 cards of your deck" / "reveal a card" — knowledge, nothing else.
    case 'reveal': {
      const to =
        node.to === 'all' ? allPlayersMask(state.players.length) : playerBit(ctx.controller);
      for (const id of anywhere(state, ctx, node)) {
        revealTo(state, id, to);
        // "Look at the top 3 cards of your deck": the cards do not move, so knowing *what*
        // they are is only half of it — `lookAtCard` is what lets the view name them where
        // they sit, for as long as the ability that looked is resolving (CR §7.9).
        lookAtCard(state, id);
      }
      return;
    }
    // "Shuffle your deck" (CR §7.8) — order only, so nothing moves and nothing is revealed.
    case 'shuffle':
      shuffleDeck(state, resolvePlayerRef(state, ctx, node.player));
      return;
    /**
     * "You may pay 1 {I}" (CR §6.1.7) — a payment inside an effect. The card that offers one
     * guards it with `can-pay-ink`, so by the time this runs the ink is there; paying less
     * than asked would be paying a cost partially, which the rules never allow, so an
     * unaffordable payment does nothing at all (CR §1.2.3).
     */
    case 'pay-ink': {
      const who = resolvePlayerRef(state, ctx, node.player ?? { kind: 'controller' });
      const amount = evalAmount(state, ctx, node.amount);
      if (amount > 0 && availableInk(state, who) >= amount) payInk(state, who, amount);
      return;
    }
    // "You may play that character for free" (CR §6.1.7) — from wherever it is now.
    case 'play-free': {
      for (const id of resolveCardRefAllIn(state, ctx, node.target)) {
        freePlay(state, id, node.exerted === true, node.destination);
      }
      return;
    }
    // "Shift that character for free" — the target nodes have already limited both picks to
    // a playable card and one of its legal bases; `rules.ts` rechecks before moving anything.
    case 'shift-free': {
      const card = resolveCardRefAllIn(state, ctx, node.target)[0];
      const onto = resolveCardRefAllIn(state, ctx, node.onto)[0];
      if (card !== undefined && onto !== undefined) freeShift(state, card, onto);
      return;
    }
    // "You may play her" — the same play, for the card's real cost (CR §4.3.3). `paidPlay`
    // takes the ink itself and refuses a play it cannot pay for in full, which is the same
    // answer the `can-pay-ink` guard on the offer gave one step earlier.
    case 'play-paying': {
      for (const id of resolveCardRefAllIn(state, ctx, node.target)) {
        paidPlay(state, id, node.exerted === true);
      }
      return;
    }
    // "Each opponent chooses and discards a card" (CR §7.3) — the discarding player decides,
    // so the frame blocks on a request addressed to *them*.
    case 'discard-chosen': {
      const who = resolvePlayerRef(state, ctx, node.player);
      const count = evalAmount(state, ctx, node.count);
      const held = playerAt(state, who).hand.length;
      const remaining = Math.min(count, held);
      if (remaining <= 0) return; // nothing to discard, so nothing to ask (CR §1.2.3)
      // A prevented discard is never asked about: the requirement itself is replaced away,
      // so no `ChoiceRequest` is raised and the hand is untouched.
      if (discardPrevented(state, who, remaining, frame.source)) return;
      frame.awaiting = { kind: 'discard', player: who, remaining };
      return;
    }
    // "…at random" (CR §7.3) — `move-card`'s random twin, and no decision to raise: the
    // game's own seeded RNG picks (SPEC.md §4.2), the same stream the deck shuffle draws
    // from, so a replay from the same seed moves the identical cards. The source is re-read
    // after every pick — a moved card is gone from it — and running dry mid-way is the
    // ordinary "do as much as you can" (CR §1.2.3). `moveCardTo` emits the named event a
    // destination deserves, so a random hand→discard is an ordinary `card-discarded`.
    case 'move-random': {
      const who = resolvePlayerRef(state, ctx, node.player);
      const count = evalAmount(state, ctx, node.count);
      // Only a hand→discard move *is* a discard (CR §7.3); any other zone pair moves as
      // usual. Offered whole, before the RNG picks anything: a prevented discard must not
      // consume the seeded stream, or a replay would diverge from that point on.
      if (node.from === 'hand' && node.to === 'discard') {
        const held = playerAt(state, who).hand.length;
        if (discardPrevented(state, who, Math.min(count, held), frame.source)) return;
      }
      for (let picked = 0; picked < count; picked += 1) {
        const pool = zoneOf(playerAt(state, who), node.from);
        if (pool.length === 0) return;
        moveCardTo(state, pool[nextIntBelow(state.rng, pool.length)] as InstanceId, {
          to: node.to,
          ...(node.placement === undefined ? {} : { placement: node.placement }),
          ...(node.exerted === undefined ? {} : { exerted: node.exerted }),
          ...(node.facedown === undefined ? {} : { facedown: node.facedown }),
          ...(node.shuffle === undefined ? {} : { shuffle: node.shuffle }),
        });
      }
      return;
    }
    // A delayed/floating triggered ability (CR §6.2.7), registered against the card whose
    // effect created it and dropped again at its duration's boundary (`bag.ts`).
    //
    // `closeOver` (ticket 106) carries named bindings from *this* frame forward onto the
    // registered `TriggerSource`, so a `bound(id)` inside `node.effect` — "play a character
    // for free … at the end of your turn, banish them" — still resolves once the trigger
    // fires as its own fresh `AbilityInstance`, long after this frame's `bindings` are gone.
    case 'delayed-trigger': {
      const closeOver =
        node.closeOver === undefined
          ? undefined
          : frame.bindings
              .filter((each) => node.closeOver?.includes(each.id) === true)
              .map((each) => ({ id: each.id, instances: [...each.instances] }));
      registerTrigger(state, {
        source: frame.source,
        when: node.when,
        effect: node.effect,
        label: node.label ?? frame.label,
        expires: node.duration,
        ...(closeOver === undefined || closeOver.length === 0 ? {} : { bindings: closeOver }),
      });
      return;
    }
    // All six leave an *applied* static behind: a modifier record scoped to the cards present
    // now, with its duration and guard. The model is `modifiers.ts` (ticket 10).
    case 'modify':
    case 'grant-keyword':
    case 'lose-keyword':
    case 'restrict':
    case 'allow':
    case 'grant-ability':
    case 'grant-subtype': {
      // Narrated, because a continuous effect is the one kind that moves nothing: without the
      // event, "your other Madrigal characters get +1 ◊" changes four cards and leaves the
      // stream saying only that an ability resolved (`events.ts`'s `modifier-applied`).
      const applied = applyModifierEffect(state, ctx, node);
      if (applied !== null) {
        emit(state, {
          type: 'modifier-applied',
          player: ctx.controller,
          source: ctx.source,
          duration: node.duration,
          ...applied,
        });
      }
      return;
    }
    // The player-scoped one ("this turn, you may ink an additional card"): the same record
    // list and the same durations, scoped to a player rather than to a snapshot of cards, so
    // it outlives the card that granted it (`modifiers.ts`).
    case 'allow-player': {
      const player = resolvePlayerRef(state, ctx, node.player ?? { kind: 'controller' });
      applyPlayerPermissionEffect(state, ctx, node, player);
      emit(state, {
        type: 'modifier-applied',
        player,
        source: ctx.source,
        instances: [],
        change: 'player-permission',
        what: node.permission,
        amount: node.amount ?? 1,
        duration: node.duration,
      });
      return;
    }
    // The restriction-side mirror of `allow-player`: a numeric ready cap given to a player
    // rather than to a card, defaulting to the ability's *opponent* — every printed instance
    // of this effect ("each opponent can't ready more than N…") speaks about the opponent.
    case 'cap-ready': {
      const player = resolvePlayerRef(state, ctx, node.player ?? { kind: 'opponent' });
      applyReadyCapEffect(state, ctx, node, player);
      emit(state, {
        type: 'modifier-applied',
        player,
        source: ctx.source,
        instances: [],
        change: 'ready-cap',
        what: 'ready',
        amount: node.amount,
        duration: node.duration,
      });
      return;
    }
    // Payment modifiers (ticket 09). They are not attached to a card in play, so they are
    // their own record kind in `costs.ts` with their own list and expiry.
    case 'modify-cost': {
      // A bound payment modifier snapshots its cards here, exactly as an applied static does
      // (`modifiers.ts`): the record follows those instances and nothing else. A reference that
      // names nobody does nothing (CR §1.2.3).
      const bound =
        node.target === undefined ? null : resolveCardRefAllIn(state, ctx, node.target);
      if (bound !== null && bound.length === 0) return;
      addCostModifier(state, {
        kind: 'amount',
        source: frame.source,
        player: resolvePlayerRef(state, ctx, node.player ?? { kind: 'controller' }),
        controller: frame.controller,
        created: state.turn,
        amount: evalAmount(state, ctx, node.amount),
        ...(node.filter === undefined ? {} : { filter: node.filter }),
        ...(bound === null ? {} : { cards: bound }),
        ...(node.applies === undefined ? {} : { applies: node.applies }),
        duration: node.duration,
        ...(node.oneShot === undefined ? {} : { oneShot: node.oneShot }),
      });
      return;
    }
    case 'grant-free-play':
      addCostModifier(state, {
        kind: 'free',
        source: frame.source,
        player: resolvePlayerRef(state, ctx, node.player ?? { kind: 'controller' }),
        controller: frame.controller,
        created: state.turn,
        filter: node.filter,
        duration: node.duration,
        ...(node.oneShot === undefined ? {} : { oneShot: node.oneShot }),
      });
      return;
    case 'forbid-play':
      addCostModifier(state, {
        kind: 'forbid',
        source: frame.source,
        player: resolvePlayerRef(state, ctx, node.player ?? { kind: 'opponent' }),
        controller: frame.controller,
        created: state.turn,
        filter: node.filter,
        duration: node.duration,
      });
      return;
    default:
      throw new Error(`Unhandled effect node ${JSON.stringify(node)}`);
  }
}

/** Where an exerted state exists (`zones.ts`) — the zones `exert` / `ready` may reach. */
const EXERTABLE_ZONES: readonly Zone[] = ['play', 'inkwell'];

/**
 * The cards an "all/each …" verb acts on: its `scope`'s pool, evaluated once here, or the
 * cards its reference names — in either case only the ones still in a zone the verb makes
 * sense in, which is play unless the verb says otherwise (CR §1.2.3).
 */
function pooled(
  state: GameState,
  ctx: AbilityContext,
  node: { readonly target?: CardRef; readonly scope?: Selector },
  zones: readonly Zone[] = ['play'],
): InstanceId[] {
  if (node.scope !== undefined) {
    return selectorPool(state, ctx, node.scope).filter((id) =>
      zones.includes(instanceAt(state, id).zone),
    );
  }
  // The same zones either way: "exert chosen card" reaches an inkwell card as much as "exert
  // all their ink" does, and which of the two a card is written with is not a rules fact.
  return node.target === undefined
    ? []
    : resolveCardRefAllIn(state, ctx, node.target).filter((id) =>
        zones.includes(instanceAt(state, id).zone),
      );
}

/**
 * The same as `pooled`, without the "must be in play" filter — for the two verbs whose whole
 * job is the cards that are *not* in play (`move-card`, `reveal`).
 */
function anywhere(
  state: GameState,
  ctx: AbilityContext,
  node: { readonly target?: CardRef; readonly scope?: Selector },
): InstanceId[] {
  if (node.scope !== undefined) return selectorPool(state, ctx, node.scope);
  return node.target === undefined ? [] : resolveCardRefAllIn(state, ctx, node.target);
}

/**
 * Which players' hands a "move these cards to a discard" leaves alone, because a replacement
 * caught the discard ("you don't discard"). The count offered per player is how many of that
 * player's hand cards the move names, so the replacement sees the whole discard at once —
 * "one or more cards" is a single event, not one per card.
 */
function preventedHands(
  state: GameState,
  moving: readonly InstanceId[],
  source: InstanceId | null,
): ReadonlySet<PlayerIndex> {
  const counts = new Map<PlayerIndex, number>();
  for (const id of moving) {
    const card = instanceAt(state, id);
    if (card.zone !== 'hand') continue;
    // Zones are keyed by controller, and a card in a hand is always its owner's anyway.
    counts.set(card.controller, (counts.get(card.controller) ?? 0) + 1);
  }
  const spared = new Set<PlayerIndex>();
  for (const [player, amount] of counts) {
    if (discardPrevented(state, player, amount, source)) spared.add(player);
  }
  return spared;
}

function inPlay(state: GameState, ctx: AbilityContext, ref: CardRef): InstanceId[] {
  return resolveCardRefAllIn(state, ctx, ref).filter(
    (id) => instanceAt(state, id).zone === 'play',
  );
}

/**
 * The continuous-effect model lives in `modifiers.ts` (SPEC.md §4.5). These are re-exported
 * here because the interpreter is where the rest of the engine reaches the ability layer.
 */
export { addModifier, applyModifierEffect, expireModifiers };

// ---------------------------------------------------------------------------
// Answering an effect frame's choices
// ---------------------------------------------------------------------------

/** Bind cards to a target spec, announcing each choice (CR §6.7.3; the Vanish substrate). */
function bindTarget(
  state: GameState,
  frame: EffectFrame,
  id: string,
  instances: readonly InstanceId[],
): void {
  const binding: TargetBinding = { id, instances: [...instances] };
  frame.bindings.push(binding);
  for (const chosen of instances) {
    const card = instanceAt(state, chosen);
    emit(state, {
      type: 'card-chosen',
      player: frame.controller,
      instance: chosen,
      defId: card.defId,
      source: frame.source,
      ability: frame.label,
    });
  }
}

/** Answer a pending `ability-target` pick. */
export function chooseTarget(state: GameState, frame: EffectFrame, instance: InstanceId): void {
  const awaiting = frame.awaiting;
  if (awaiting === null || awaiting.kind !== 'target') {
    throw new Error('No target choice is pending');
  }
  awaiting.picked.push(instance);
  const wanted = evalAmount(state, contextOf(frame), awaiting.spec.count ?? 1);
  if (awaiting.picked.length >= wanted) finishTarget(state, frame);
}

/** Answer a pending `ability-target` with "that's all" — the "up to N" and empty-pool case. */
export function finishTarget(state: GameState, frame: EffectFrame): void {
  const awaiting = frame.awaiting;
  if (awaiting === null || awaiting.kind !== 'target') {
    throw new Error('No target choice is pending');
  }
  frame.awaiting = null;
  if (awaiting.picked.length === 0) return; // nothing chosen ⇒ the sentence does nothing
  bindTarget(state, frame, awaiting.spec.id, awaiting.picked);
  unshift(frame, awaiting.then);
}

/**
 * Which of a `distribute-remove-damage` pool's `selector` candidates are still worth naming —
 * in play, currently damaged (an undamaged character has nothing this decision can remove),
 * and not already allocated to this round (ticket 135).
 */
function distributeCandidates(
  state: GameState,
  ctx: AbilityContext,
  spec: DistributeSpec,
  picked: readonly InstanceId[],
): InstanceId[] {
  return selectorPool(state, ctx, spec.selector).filter(
    (id) =>
      instanceAt(state, id).zone === 'play' &&
      instanceAt(state, id).damage > 0 &&
      !picked.includes(id),
  );
}

/** Answer a pending distribute-a-pool "which character next" pick (ticket 135). */
export function chooseDistributeTarget(frame: EffectFrame, instance: InstanceId): void {
  const awaiting = frame.awaiting;
  if (awaiting === null || awaiting.kind !== 'distribute' || awaiting.pendingTarget !== null) {
    throw new Error('No distribute-target choice is pending');
  }
  awaiting.pendingTarget = instance;
}

/**
 * Answer a pending distribute-a-pool "how much to this one" pick (ticket 135) — spent
 * immediately through the same `removeDamage` chokepoint `remove-damage` uses, so a
 * replacement effect sees and can rewrite it exactly the same way. The **actual** amount
 * removed (not the requested one) is what shrinks the pool and accumulates into `spent`, the
 * same discipline ticket 100's `as` binding follows.
 */
export function chooseDistributeAmount(state: GameState, frame: EffectFrame, amount: number): void {
  const awaiting = frame.awaiting;
  if (awaiting === null || awaiting.kind !== 'distribute' || awaiting.pendingTarget === null) {
    throw new Error('No distribute-amount choice is pending');
  }
  const target = awaiting.pendingTarget;
  const before = state.events.length;
  removeDamage(state, target, amount);
  const removed = state.events
    .slice(before)
    .reduce((total, event) => (event.type === 'damage-removed' ? total + event.amount : total), 0);
  awaiting.spent += removed;
  awaiting.remaining = Math.max(0, awaiting.remaining - removed);
  awaiting.picked.push(target);
  awaiting.pendingTarget = null;
  // "Do as much as you can" (CR §1.2.3): stop asking once the pool is spent or nobody left
  // carries damage to remove. The player can still stop earlier via `ability-target-done`.
  if (
    awaiting.remaining <= 0 ||
    distributeCandidates(state, contextOf(frame), awaiting.spec, awaiting.picked).length === 0
  ) {
    finishDistribute(frame);
  }
}

/** Stop distributing — "up to N" chose less, the pool ran out, or nobody eligible is left. */
export function finishDistribute(frame: EffectFrame): void {
  const awaiting = frame.awaiting;
  if (awaiting === null || awaiting.kind !== 'distribute') {
    throw new Error('No distribute choice is pending');
  }
  frame.awaiting = null;
  if (awaiting.as !== undefined) {
    frame.resultBindings.push({ id: awaiting.as, amount: awaiting.spent });
  }
}

/** Bind a player to a `choose-player` spec, announcing the choice (ticket 102). */
function bindPlayer(state: GameState, frame: EffectFrame, id: string, player: PlayerIndex): void {
  const binding: PlayerBinding = { id, player };
  frame.playerBindings.push(binding);
  emit(state, {
    type: 'player-chosen',
    player: frame.controller,
    chosen: player,
    source: frame.source,
    ability: frame.label,
  });
}

/** Answer a pending `choose-player` pick. */
export function choosePlayer(state: GameState, frame: EffectFrame, player: PlayerIndex): void {
  const awaiting = frame.awaiting;
  if (awaiting === null || awaiting.kind !== 'choose-player') {
    throw new Error('No player choice is pending');
  }
  frame.awaiting = null;
  bindPlayer(state, frame, awaiting.spec.id, player);
  unshift(frame, awaiting.then);
}

/**
 * Answer a pending "you may" (CR §6.1.4). Declining runs the node's `otherwise` when it has
 * one — the printed "unless" — and otherwise skips the rest of the sentence, as ever.
 *
 * A decline with nothing to fall to is narrated (`effect-skipped`), for the same reason an
 * unmet condition is: the ability still resolves, still empties out of the bag, and still
 * moves nothing, and "Support resolved" is a worse answer than "the offer was declined" —
 * especially across the table, where the decline was somebody else's.
 */
export function answerMay(state: GameState, frame: EffectFrame, accept: boolean): void {
  const awaiting = frame.awaiting;
  if (awaiting === null || awaiting.kind !== 'may') throw new Error('No may choice is pending');
  frame.awaiting = null;
  if (accept) unshift(frame, awaiting.then);
  else if (awaiting.otherwise !== undefined) unshift(frame, awaiting.otherwise);
  else skipped(state, frame, { reason: 'declined' });
}

/** Answer a pending "choose one —" (CR §6.1.3). */
export function answerMode(frame: EffectFrame, mode: number): void {
  const awaiting = frame.awaiting;
  if (awaiting === null || awaiting.kind !== 'modal') throw new Error('No mode choice is pending');
  const chosen = awaiting.modes[mode];
  if (chosen === undefined) throw new Error(`No mode ${mode} on this ability`);
  frame.awaiting = null;
  unshift(frame, chosen.effect);
}
